import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Select, Space, Typography, message } from 'antd';
import type { RouteViewModel } from '../../../types/routeViewModel';
import { useUpdateRouteMutation } from '../../../store/api/routesApi';
import {
  useGetDriversQuery,
  useGetVehiclesQuery
} from '../../../store/api/referencesApi';
import { MutationReasonField } from '../../mutationWorkspace/MutationReasonField';
import type { CorrectionContext } from '../hooks/useCorrectionContext';
import { mapRejectMessage } from '../../mutationWorkspace/buildMutationPolicyPreview';

const { Text } = Typography;

type Props = {
  routeId: number;
  view: RouteViewModel;
  ctx: CorrectionContext;
  onSuccess: () => void;
};

export function ResourceCorrectionPanel({ routeId, view, ctx, onSuccess }: Props) {
  const [driverId, setDriverId] = useState<number | undefined>(
    (view.parties.driver as { id?: number } | null)?.id ?? undefined
  );
  const [vehicleId, setVehicleId] = useState<number | undefined>(
    (view.parties.vehicle as { id?: number } | null)?.id ?? undefined
  );
  const [trailerVehicleId, setTrailerVehicleId] = useState<number | undefined>(
    (view.parties.trailerVehicle as { id?: number } | null)?.id ?? undefined
  );
  const [mutationReason, setMutationReason] = useState<string | null>(null);

  const { data: drivers = [] } = useGetDriversQuery();
  const { data: vehicles = [] } = useGetVehiclesQuery();
  const [updateRoute, { isLoading }] = useUpdateRouteMutation();

  useEffect(() => {
    setDriverId((view.parties.driver as { id?: number } | null)?.id ?? undefined);
    setVehicleId((view.parties.vehicle as { id?: number } | null)?.id ?? undefined);
    setTrailerVehicleId((view.parties.trailerVehicle as { id?: number } | null)?.id ?? undefined);
    setMutationReason(null);
  }, [view.parties.driver, view.parties.vehicle, view.parties.trailerVehicle, view.builtAt]);

  const canDriver = ctx.isOperationAllowed('RESOURCE_DRIVER');
  const canVehicle = ctx.isOperationAllowed('RESOURCE_VEHICLE');
  const canTrailer = ctx.isOperationAllowed('RESOURCE_TRAILER');

  if (!canDriver && !canVehicle && !canTrailer) {
    return (
      <Text type="secondary">Изменение ресурсов недоступно для текущего статуса маршрута.</Text>
    );
  }

  const handleSave = async () => {
    if (!mutationReason) {
      message.error('Укажите причину изменения');
      return;
    }

    const payload: {
      id: number;
      expectedUpdatedAt: string;
      mutationReason: string;
      driverId?: number;
      vehicleId?: number;
      trailerVehicleId?: number;
    } = {
      id: routeId,
      expectedUpdatedAt: ctx.routeUpdatedAt,
      mutationReason
    };

    if (canDriver) payload.driverId = driverId;
    if (canVehicle) payload.vehicleId = vehicleId;
    if (canTrailer) payload.trailerVehicleId = trailerVehicleId;

    try {
      await updateRoute(payload).unwrap();
      message.success('Ресурсы маршрута обновлены');
      setMutationReason(null);
      onSuccess();
    } catch (e: unknown) {
      const err = e as { data?: unknown; status?: number };
      const msg =
        err.status === 409
          ? 'Маршрут был изменён другим пользователем. Обновите карточку и повторите.'
          : mapRejectMessage(err.data) ?? 'Не удалось обновить ресурсы';
      message.error(msg);
    }
  };

  return (
    <div className="mutation-workspace__section">
      <div className="mutation-workspace__section-title">Ресурсы</div>
      <Form layout="vertical">
        {canDriver && (
          <Form.Item label="Водитель">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={driverId}
              onChange={(v) => setDriverId(v ?? undefined)}
              options={drivers.map((d) => ({
                value: d.id,
                label: d.fullName || `#${d.id}`
              }))}
            />
          </Form.Item>
        )}
        {canVehicle && (
          <Form.Item label="Тягач">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={vehicleId}
              onChange={(v) => setVehicleId(v ?? undefined)}
              options={vehicles.map((v) => ({
                value: v.id,
                label: v.registrationNumber ?? `#${v.id}`
              }))}
            />
          </Form.Item>
        )}
        {canTrailer && (
          <Form.Item label="Прицеп">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={trailerVehicleId}
              onChange={(v) => setTrailerVehicleId(v ?? undefined)}
              options={vehicles.map((v) => ({
                value: v.id,
                label: v.registrationNumber ?? `#${v.id}`
              }))}
            />
          </Form.Item>
        )}
      </Form>

      <MutationReasonField value={mutationReason} onChange={setMutationReason} disabled={isLoading} />

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" loading={isLoading} onClick={handleSave}>
          Сохранить ресурсы
        </Button>
      </Space>

      {ctx.correction.executionLock.inTransit && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="Изменение ресурсов в пути будет зафиксировано в журнале маршрута."
        />
      )}
    </div>
  );
}
